import { createContext, type ReactNode, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useAtomValue, useSetAtom } from 'jotai';
import { atom } from 'jotai/vanilla';
import { trackEvent } from '@zep/logger/tracking';
import { zusToast } from '@zep/toast';
import { useLocale } from '@/hooks';
import { useBuildInfoContext } from '@/views/build/context';
import { editingQuizIdAtom } from '../../../components/focus/editModeAtom';
import { useQuizList } from '../../../components/useQuizList';
import { useBuildFormContext } from '../../../form/schema';
import { type BuildQuizListItem } from '../../../form/schema/BuildQuizListSchema';
import { useBuildQuestionAlert } from '../../../hook/useBuildQuestionAlert';
import { AI_BUILDER_CHAT_PLACEHOLDER, initialAiBuilderChatState } from '../consts';
import { useActiveSourceRefs, useAiBuilderBuilderContext } from '../hooks';
import { useAiBuilderChatCopy } from '../hooks/useAiBuilderChatCopy';
import { useAiBuilderComposerSignals } from '../hooks/useAiBuilderComposerSignals';
import { useAiBuilderQuizListMutations } from '../hooks/useAiBuilderQuizListMutations';
import { useAiBuilderRegisterServerSource } from '../hooks/useAiBuilderRegisterServerSource';
import { useAiBuilderResponseFeedback } from '../hooks/useAiBuilderResponseFeedback';
import { useAiBuilderSelectedQuizContext } from '../hooks/useAiBuilderSelectedQuizContext';
import { useAiBuilderSubmitAssistantAction } from '../hooks/useAiBuilderSubmitAssistantAction';
import { useAiBuilderSubmitChoice } from '../hooks/useAiBuilderSubmitChoice';
import { useAiBuilderSubmitConfirm } from '../hooks/useAiBuilderSubmitConfirm';
import { useAiBuilderSubmitPrompt } from '../hooks/useAiBuilderSubmitPrompt';
import { useAiBuilderSubmitUndoLastAiAction } from '../hooks/useAiBuilderSubmitUndoLastAiAction';
import { useAiBuilderUserMessageRetry } from '../hooks/useAiBuilderUserMessageRetry';
import { type createUserAttachment } from '../stores/aiBuilderAttachments';
import { aiBuilderChatStateAtom, type AiBuilderChatStore, AiBuilderChatStoreContext, createAiBuilderChatStore } from '../stores/aiBuilderChatStore';
import { appendAiBuilderUserMessage, appendQuizCardActionChoiceMessage, clearAiBuilderTurnErrorMessages, isUnsubmittedQuizCardActionChoiceMessage, removeUnsubmittedQuizCardActionChoiceMessages, settleSendingAiBuilderUserMessages, updateAiBuilderUserMessageStatus } from '../stores/aiBuilderMessageState';
import { applyAiBuilderMessageEvent, getActiveConfirmMessage, getActiveSelectedChoiceMessage, stopRunningProgressMessages, updateChoiceOptionSelection } from '../stores/aiBuilderMessageStream';
import { useOptionalAiBuilderSessionStore } from '../stores/AiBuilderSessionProvider';
import { isAiBuilderTurnBusy } from '../stores/aiBuilderTurnRequests';
import { isAiBuilderUsageBlocked } from '../stores/aiBuilderUsagePolicy';
import { type AiBuilderChatActions, type AiBuilderChatState, type AiBuilderMessage, type AiBuilderSelectedQuizContext, type AiBuilderSubmitPromptOptions, type AiBuilderUsageInfo, type AiBuilderUserMessageRetryRequest, type OpenAiBuilderChatParams } from '../stores/types';
import { useAiBuilderChatRestoreSession } from '../stores/useAiBuilderChatRestoreSession';
import { useAiBuilderChatOperationSync, useRegisterAiBuilderChatSessionPort } from '../stores/useAiBuilderChatSessionSharing';
import { type UpdateAiBuilderUserMessageStatus, useAiBuilderTurnAbortController, useDispatchServerTurn, useRunAiBuilderEventsSafely } from '../stores/useAiBuilderServerTurnDispatcher';
import { type EnsureServerSessionOptions, useAiBuilderSharedSession } from '../stores/useAiBuilderSharedSession';
import { useAiBuilderTurnRunner } from '../stores/useAiBuilderTurnRunner';
import { handleAiBuilderAuthExpired, isAiBuilderAuthExpiredError } from '../utils/aiBuilderAuthExpiry';
import { aiBuilderChatAdapter } from '../utils/aiBuilderChatAdapter';
import { type AiBuilderChatCopy } from '../utils/aiBuilderChatCopy';
import { type AiBuilderChatRestoreSnapshot } from '../utils/aiBuilderChatRestore';
import { trackAiBuilderUsageBlocked } from '../utils/aiBuilderChatTracking';
import { questionListRevisionOf, serializeCurrentQuizListToAiBuilderQuestions } from '../utils/aiBuilderQuestionSnapshot';
import { getAiBuilderQuestionState } from '../utils/aiBuilderWelcomePolicy';
import { type AiBuilderAuthExpiredPlace } from '../utils/events.quiz-ai-builder';
import { type AiBuilderBuilderContext, type AiBuilderChatEvent, type AiBuilderTurnRequest } from '../utils/types';
type UpdateAiBuilderChatState = (updater: (prev: AiBuilderChatState) => AiBuilderChatState) => void;
type AiBuilderUserMessageStatus = Parameters<UpdateAiBuilderUserMessageStatus>[1];
const isSameAiBuilderUsage = (left: AiBuilderChatState['usage'], right: AiBuilderChatState['usage']): boolean => {
  if (left === right) return true;
  if (!left || !right) return left === right;
  return left.state === right.state && left.remaining === right.remaining && left.limit === right.limit;
};
const settleCanceledAiBuilderWorkMessages = (messages: AiBuilderMessage[], copy: AiBuilderChatCopy): AiBuilderMessage[] => stopRunningProgressMessages({
  copy,
  messages: settleSendingAiBuilderUserMessages(messages),
  stoppedMessage: {
    id: `assistant-cancel-${Date.now()}`,
    kind: 'assistant',
    content: copy.system.requestCanceled
  }
});
export const AiBuilderChatComposerSignalContext = createContext<AiBuilderChatComposerSignalState | null>(null);
export const AiBuilderChatActionsContext = createContext<AiBuilderChatActions | null>(null);
type AiBuilderChatComposerSignalState = {
  focusRequestId: number;
  filePickerRequestId: number;
  confirmActionAttentionRequestId: number;
  confirmActionAttentionMessageId: string | null;
};
type CreateTurnContextParams = {
  signal?: AbortSignal;
  selectedQuizContext?: AiBuilderSelectedQuizContext | null;
  sourceRefs?: string[];
  turnHints?: AiBuilderSubmitPromptOptions['turnHints'];
};
type AiBuilderChatEnvironment = {
  builderContext: AiBuilderBuilderContext;
  getValues: ReturnType<typeof useBuildFormContext>['getValues'];
  locale: string;
  /** 유저 국가 코드 — 서버 성취기준 검색 게이트 판정 축 (세션 계약 전달용). */
  userCountryCode: string;
};
const aiBuilderChatEnvironmentAtom = atom<AiBuilderChatEnvironment | null>(null);
const useAppendRunRejectionMessage = ({
  activeRunIdRef,
  copy,
  updateState
}: {
  activeRunIdRef: {
    current: number;
  };
  copy: AiBuilderChatCopy;
  updateState: UpdateAiBuilderChatState;
}) => {
  return useCallback((runId: number) => {
    if (activeRunIdRef.current !== runId) return;
    updateState(prev => ({
      ...prev,
      messages: [...prev.messages.map(message => {
        if (message.kind !== 'progress' || message.status !== 'running') {
          return message;
        }
        return {
          ...message,
          status: 'stopped' as const,
          title: copy.system.runRejectedProgressTitle,
          steps: message.steps.map(step => step.status !== 'done' ? {
            ...step,
            status: 'stopped' as const
          } : step)
        };
      }), {
        id: `system-error-${runId}-${Date.now()}`,
        kind: 'system',
        tone: 'error',
        content: copy.system.temporaryError
      }]
    }));
  }, [activeRunIdRef, copy, updateState]);
};
const useUpdateAiBuilderUserMessageStatus = (updateState: UpdateAiBuilderChatState) => {
  return useCallback((messageId: string, status: AiBuilderUserMessageStatus) => {
    updateState(prev => ({
      ...prev,
      messages: updateAiBuilderUserMessageStatus({
        messages: prev.messages,
        messageId,
        status
      })
    }));
  }, [updateState]);
};
export const AiBuilderChatProvider = ({
  children,
  initialUsage = null
}: {
  children?: ReactNode;
  initialUsage?: AiBuilderUsageInfo | null;
}) => {
  const {
    getValues,
    setValue
  } = useBuildFormContext();
  const builderContext = useAiBuilderBuilderContext();
  const {
    action,
    userCountryCode
  } = useBuildInfoContext();
  const {
    locale
  } = useLocale();
  const copy = useAiBuilderChatCopy();
  const {
    quizList,
    replaceQuizList
  } = useQuizList();
  const currentQuestionState = getAiBuilderQuestionState({
    action,
    questionCount: quizList.length
  });
  const currentQuestionStateRef = useRef(currentQuestionState);
  currentQuestionStateRef.current = currentQuestionState;
  const {
    openQuestionCountLimitAlert
  } = useBuildQuestionAlert();
  const editingQuizId = useAtomValue(editingQuizIdAtom);
  const setEditingQuizId = useSetAtom(editingQuizIdAtom);
  const aiBuilderChatStoreRef = useRef<AiBuilderChatStore | null>(null);
  const initialChatState = useMemo(() => ({
    ...initialAiBuilderChatState,
    usage: initialUsage
  }), [initialUsage]);
  if (!aiBuilderChatStoreRef.current) {
    aiBuilderChatStoreRef.current = createAiBuilderChatStore(initialChatState);
  }
  const aiBuilderChatStore = aiBuilderChatStoreRef.current;
  useLayoutEffect(() => {
    aiBuilderChatStore.set(aiBuilderChatEnvironmentAtom, {
      builderContext,
      getValues,
      locale,
      userCountryCode
    });
  }, [aiBuilderChatStore, builderContext, getValues, locale, userCountryCode]);
  const getChatState = useCallback(() => aiBuilderChatStore.get(aiBuilderChatStateAtom), [aiBuilderChatStore]);
  const getChatEnvironment = useCallback(() => {
    const environment = aiBuilderChatStore.get(aiBuilderChatEnvironmentAtom);
    if (!environment) {
      throw new Error('AiBuilderChatProvider environment is not initialized.');
    }
    return environment;
  }, [aiBuilderChatStore]);
  const [state, setState] = useState(initialChatState);
  const {
    composerSignalState,
    requestConfirmActionAttention,
    requestComposerEntrySignal,
    requestComposerFocus,
    resetComposerSignalsForOpenPanel,
    resetFilePickerRequest
  } = useAiBuilderComposerSignals();
  const activeSourceRefs = useActiveSourceRefs();
  const activeRunIdRef = useRef(0);
  const turnAbortController = useAiBuilderTurnAbortController();
  const requestIdRef = useRef(0);
  const lastQuizListSnapshotRef = useRef<BuildQuizListItem[] | null>(null);
  const cancelActiveTurn = useCallback(() => {
    activeRunIdRef.current += 1;
    turnAbortController.abort();
  }, [turnAbortController]);
  const {
    syncChatActiveOperation,
    holdSourceRegistration
  } = useAiBuilderChatOperationSync();
  const updateState = useCallback((updater: (prev: AiBuilderChatState) => AiBuilderChatState) => {
    const nextState = updater(getChatState());
    aiBuilderChatStore.set(aiBuilderChatStateAtom, nextState);
    setState(nextState);
    syncChatActiveOperation(nextState);
  }, [aiBuilderChatStore, getChatState, syncChatActiveOperation]);

  /**
   * 복원된 대화 뒤에서 id 발급을 이어받는다. 재로그인은 hard navigation 이라 Provider
   * 가 새로 마운트되며 두 카운터가 0 으로 돌아가는데, 그대로 두면 복원 직후 첫
   * 메시지가 복원된 `user-1` 과 같은 id 를 갖는다.
   */
  const seedAiBuilderIdCounters = useCallback((snapshot: AiBuilderChatRestoreSnapshot) => {
    requestIdRef.current = Math.max(requestIdRef.current, snapshot.nextRequestId - 1);
    activeRunIdRef.current = Math.max(activeRunIdRef.current, snapshot.nextRunId - 1);
  }, []);
  const {
    draftInstanceId,
    hasPendingRestore
  } = useAiBuilderChatRestoreSession({
    updateState,
    onRestored: seedAiBuilderIdCounters
  });
  useEffect(() => {
    return cancelActiveTurn;
  }, [cancelActiveTurn]);
  useEffect(() => {
    updateState(prev => {
      if (prev.sessionId || prev.turnInProgress || prev.isGenerating) {
        return prev;
      }
      if (isSameAiBuilderUsage(prev.usage, initialUsage)) {
        return prev;
      }
      return {
        ...prev,
        usage: initialUsage
      };
    });
  }, [initialUsage, updateState]);
  const selectedQuizContext = useAiBuilderSelectedQuizContext({
    editingQuizId,
    getValues,
    isGenerating: state.isGenerating,
    quizList,
    turnInProgress: state.turnInProgress
  });
  const getCurrentQuestionState = useCallback(() => currentQuestionStateRef.current, []);
  const {
    ensureSharedSession,
    startNewSessionEpoch
  } = useAiBuilderSharedSession({
    draftInstanceId,
    getChatEnvironment,
    getChatState,
    hasPendingRestore,
    updateState
  });

  /** 채팅 턴용 — 공유 세션을 확보하되, 그 사이 이 턴이 취소됐으면 null 로 낡은 턴을 끝낸다(세션은 저장돼 남는다). */
  const ensureServerSession = useCallback(async (runId: number, options: EnsureServerSessionOptions = {}): Promise<string | null> => {
    const sessionId = await ensureSharedSession(options);
    if (activeRunIdRef.current !== runId) return null;
    return sessionId;
  }, [ensureSharedSession]);
  useRegisterAiBuilderChatSessionPort({
    draftInstanceId,
    ensureSession: ensureSharedSession,
    getChatState
  });
  useEffect(() => {
    const storeState = getChatState();
    if (storeState.selectedQuizContext === selectedQuizContext) {
      return;
    }
    const nextState = {
      ...storeState,
      selectedQuizContext
    };
    aiBuilderChatStore.set(aiBuilderChatStateAtom, nextState);
    setState(nextState);
  }, [aiBuilderChatStore, getChatState, selectedQuizContext, state]);
  const openFromEntry = useCallback((params: OpenAiBuilderChatParams) => {
    const currentState = getChatState();
    const hasDraft = currentState.draft.trim().length > 0;
    const usageBlocked = isAiBuilderUsageBlocked(currentState.usage);
    activeSourceRefs.clear();
    if (hasDraft && (params.entry === 'youtube' || params.entry === 'website')) {
      // 아이콘 없는 중립 안내 + 위치·노출 시간은 rht `toast()` 기본값(top-right·4초) 보존.
      zusToast.success(copy.toast.sourceDraftConflict, {
        icon: null,
        position: 'top-right',
        duration: 4000
      });
    }
    updateState(prev => {
      // 문제 카드 진입은 자유 입력 대신 선택 문제 기준 후속 선택지를 먼저 띄운다.
      // 소진/turn 진행 중에는 선택지 제출이 어차피 막히므로 붙이지 않는다.
      const shouldAppendQuizCardChoice = params.source === 'quiz_card' && !isAiBuilderUsageBlocked(prev.usage) && !isAiBuilderTurnBusy(prev);
      const baseState = {
        ...prev,
        panelOpen: true,
        conversationEntryIntent: prev.conversationEntryIntent ?? params.entry,
        conversationQuestionState: prev.messages.length > 0 && prev.conversationQuestionState ? prev.conversationQuestionState : getCurrentQuestionState(),
        ...(shouldAppendQuizCardChoice ? {
          messages: appendQuizCardActionChoiceMessage({
            messages: prev.messages,
            copy
          })
        } : {})
      };

      // 소진 상태로 진입하면 소진 UI(인트로/카드)가 안내를 담당하므로 draft만
      // 비우고 입력은 잠근다.
      if (isAiBuilderUsageBlocked(prev.usage)) {
        return {
          ...baseState,
          draft: '',
          selectedFile: null,
          placeholder: AI_BUILDER_CHAT_PLACEHOLDER.default
        };
      }
      if (params.entry === 'file') {
        return {
          ...baseState,
          placeholder: AI_BUILDER_CHAT_PLACEHOLDER.file
        };
      }
      if (params.entry === 'youtube' && !hasDraft) {
        return {
          ...baseState,
          placeholder: AI_BUILDER_CHAT_PLACEHOLDER.youtube
        };
      }
      if (params.entry === 'website' && !hasDraft) {
        return {
          ...baseState,
          placeholder: AI_BUILDER_CHAT_PLACEHOLDER.website
        };
      }
      return {
        ...baseState,
        placeholder: AI_BUILDER_CHAT_PLACEHOLDER.default
      };
    });
    if (usageBlocked) {
      trackAiBuilderUsageBlocked(currentState.usage, 'open');
    } else {
      requestComposerEntrySignal(params.entry);
      void ensureServerSession(activeRunIdRef.current, {
        trackBlockedOnOpen: true
      }).catch(() => {
        // Opening the panel should not fail the chat. The next turn still
        // performs the authoritative usage check and surfaces any error.
      });
    }
  }, [activeSourceRefs, copy, ensureServerSession, getChatState, getCurrentQuestionState, requestComposerEntrySignal, updateState]);
  const closePanel = useCallback(() => {
    cancelActiveTurn();
    activeSourceRefs.clear();
    const shouldClearQuizCardEditingQuizId = Boolean(editingQuizId) && getChatState().messages.some(isUnsubmittedQuizCardActionChoiceMessage);
    if (shouldClearQuizCardEditingQuizId) {
      setEditingQuizId(null);
    }
    updateState(prev => ({
      ...prev,
      isGenerating: false,
      turnInProgress: false,
      panelOpen: false,
      messages: removeUnsubmittedQuizCardActionChoiceMessages(isAiBuilderTurnBusy(prev) ? settleCanceledAiBuilderWorkMessages(prev.messages, copy) : prev.messages)
    }));
    resetFilePickerRequest();
  }, [activeSourceRefs, cancelActiveTurn, copy, editingQuizId, getChatState, resetFilePickerRequest, setEditingQuizId, updateState]);
  const setDraft = useCallback((draft: string) => {
    updateState(prev => prev.draft === draft ? prev : {
      ...prev,
      draft
    });
  }, [updateState]);
  const setSelectedFile = useCallback((file: AiBuilderChatState['selectedFile']) => {
    activeSourceRefs.clear();
    updateState(prev => ({
      ...prev,
      selectedFile: file
    }));
  }, [activeSourceRefs, updateState]);
  const appendSystemErrorMessage = useCallback((content: string) => {
    updateState(prev => ({
      ...prev,
      messages: [...prev.messages, {
        id: `system-error-${Date.now()}`,
        kind: 'system',
        tone: 'error',
        content
      }]
    }));
  }, [updateState]);
  const handleAuthExpired = useCallback((place: AiBuilderAuthExpiredPlace) => {
    handleAiBuilderAuthExpired({
      getChatState,
      draftInstanceId,
      place,
      nextRequestId: requestIdRef.current + 1,
      nextRunId: activeRunIdRef.current + 1
    });
  }, [draftInstanceId, getChatState]);

  /**
   * 만료가 도착한 채널을 계측 축으로 남기기 위해 소비처별로 place 를 고정해 둔다.
   * 하위 훅은 자기 위치만 알면 되고, 참조도 안정적이라 deps 를 흔들지 않는다.
   */
  const authExpiredHandlers = useMemo(() => ({
    sessionReset: () => handleAuthExpired('session_reset'),
    sourceAttach: () => handleAuthExpired('source_attach'),
    sourcePreparation: () => handleAuthExpired('source_preparation'),
    turnRequest: () => handleAuthExpired('turn_request'),
    turnStream: () => handleAuthExpired('turn_stream')
  }), [handleAuthExpired]);
  const resetAdapterSession = useCallback((sessionId: string | null) => {
    if (!sessionId) {
      return;
    }
    void aiBuilderChatAdapter.resetSession({
      sessionId,
      draftInstanceId
    }).catch((error: unknown) => {
      // 세션 리셋도 JSON 경로다 — 만료를 일반 초기화 실패로 뭉개지 않는다.
      if (isAiBuilderAuthExpiredError(error)) {
        authExpiredHandlers.sessionReset();
        return;
      }
      appendSystemErrorMessage(copy.system.resetSessionFailed);
    });
  }, [appendSystemErrorMessage, authExpiredHandlers, copy, draftInstanceId]);
  const appendUserMessage = useCallback(({
    attachment,
    consumeChoiceMessageId,
    content,
    requestId,
    retry
  }: {
    attachment?: ReturnType<typeof createUserAttachment>;
    consumeChoiceMessageId?: string;
    content: string;
    requestId: number;
    retry?: AiBuilderUserMessageRetryRequest;
  }) => {
    updateState(prev => ({
      ...prev,
      draft: '',
      selectedFile: null,
      placeholder: AI_BUILDER_CHAT_PLACEHOLDER.default,
      messages: appendAiBuilderUserMessage({
        messages: prev.messages,
        attachment,
        consumeChoiceMessageId,
        content,
        requestId,
        retry
      })
    }));
  }, [updateState]);
  const appendAssistantMessage = useCallback((message: Extract<AiBuilderMessage, {
    kind: 'assistant';
  }>) => {
    updateState(prev => ({
      ...prev,
      messages: [...prev.messages, message]
    }));
  }, [updateState]);
  const updateUserMessageStatus = useUpdateAiBuilderUserMessageStatus(updateState);
  const beginUserMessageRetry = useCallback((messageId: string) => {
    updateState(prev => ({
      ...prev,
      messages: updateAiBuilderUserMessageStatus({
        messages: clearAiBuilderTurnErrorMessages({
          messages: prev.messages,
          userMessageId: messageId
        }),
        messageId,
        status: 'sending'
      })
    }));
  }, [updateState]);
  const sessionStore = useOptionalAiBuilderSessionStore();
  const isTurnBusy = useCallback(() => {
    // 생성기가 같은 서버 세션을 쓰는 동안엔 채팅 턴·액션(선택지·확인·재시도·되돌리기)도 막는다 — SESSION_LOCKED 방지.
    if (sessionStore?.getActiveOperation() === 'generator') return true;
    const {
      isGenerating,
      turnInProgress
    } = getChatState();
    return isAiBuilderTurnBusy({
      isGenerating,
      turnInProgress
    });
  }, [getChatState, sessionStore]);
  const getLatestMessages = useCallback(() => getChatState().messages, [getChatState]);
  const {
    applyGeneratedQuizzes,
    focusQuizByNumber,
    replaceQuestionSnapshots,
    resetLastQuizListSnapshot,
    scrollQuizByQuestionRef
  } = useAiBuilderQuizListMutations({
    appendAssistantMessage,
    copy,
    getValues,
    lastQuizListSnapshotRef,
    openQuestionCountLimitAlert,
    replaceQuizList,
    setValue
  });
  const resetLocalChatSessionState = useCallback(() => {
    cancelActiveTurn();
    // 새 대화 — 진행 중이던 세션 생성이 끝나도 이 대화에 저장하지 않는다.
    startNewSessionEpoch();
    requestIdRef.current = 0;
    resetLastQuizListSnapshot();
    activeSourceRefs.clear();
    setEditingQuizId(null);
    updateState(prev => ({
      ...initialAiBuilderChatState,
      panelOpen: true,
      conversationEntryIntent: 'chat',
      conversationQuestionState: getCurrentQuestionState(),
      usage: prev.usage ?? initialUsage
    }));
    resetComposerSignalsForOpenPanel();
  }, [activeSourceRefs, cancelActiveTurn, getCurrentQuestionState, resetLastQuizListSnapshot, resetComposerSignalsForOpenPanel, initialUsage, setEditingQuizId, updateState, startNewSessionEpoch]);
  const restartConversation = useCallback(() => {
    // 생성기가 같은 세션을 쓰는 동안엔 새 대화로 넘기지 않는다 — 세션 세대가 올라가 생성기가 기다리던 세션을
    // 버리게 된다(SESSION_UNAVAILABLE). 메뉴도 그동안 꺼 둔다(AiBuilderChatPanelHeader).
    if (sessionStore?.getActiveOperation() === 'generator') return;
    const {
      sessionId,
      messages
    } = getChatState();
    trackEvent('quiz_ai_builder_chat_restart', {
      session_id: sessionId ?? '',
      message_count: messages.length
    });
    resetLocalChatSessionState();
  }, [getChatState, resetLocalChatSessionState, sessionStore]);
  const applyAiBuilderEvent = useCallback((event: Extract<AiBuilderChatEvent, {
    type: 'append';
  } | {
    type: 'replace';
  }>) => {
    updateState(prev => ({
      ...prev,
      messages: applyAiBuilderMessageEvent(prev.messages, event)
    }));
    if (event.message.kind === 'confirm') {
      const targetQuestionRef = event.message.questionRefs?.[0];
      if (targetQuestionRef) {
        scrollQuizByQuestionRef(targetQuestionRef);
      }
    }
  }, [scrollQuizByQuestionRef, updateState]);
  const runAiBuilderEvents = useAiBuilderTurnRunner({
    activeRunIdRef,
    updateState,
    requestComposerFocus,
    applyGeneratedQuizzes,
    replaceQuestionSnapshots,
    applyAiBuilderEvent,
    onAuthExpired: authExpiredHandlers.turnStream
  });
  const appendRunRejectionMessage = useAppendRunRejectionMessage({
    activeRunIdRef,
    copy,
    updateState
  });
  const runAiBuilderEventsSafely = useRunAiBuilderEventsSafely({
    activeRunIdRef,
    appendRunRejectionMessage,
    isAuthExpiredError: isAiBuilderAuthExpiredError,
    onAuthExpired: authExpiredHandlers.turnRequest,
    runAiBuilderEvents,
    turnAbortController,
    updateUserMessageStatus
  });
  const dispatchServerTurn = useDispatchServerTurn({
    runAiBuilderEventsSafely,
    turnAbortController
  });

  // runId keeps stale events out of UI state; AbortController owns the network stream.
  const createTurnContext = useCallback(({
    signal,
    selectedQuizContext: selectedQuizContextOverride,
    sourceRefs = [],
    turnHints
  }: CreateTurnContextParams = {}): AiBuilderTurnRequest['context'] => {
    const {
      selectedQuizContext: currentSelectedQuizContext,
      sessionId
    } = getChatState();
    const {
      builderContext: currentBuilderContext,
      getValues: getCurrentValues,
      locale: currentLocale,
      userCountryCode: currentUserCountryCode
    } = getChatEnvironment();
    const selectedQuestionContext = selectedQuizContextOverride === undefined ? currentSelectedQuizContext : selectedQuizContextOverride;
    const questions = serializeCurrentQuizListToAiBuilderQuestions(getCurrentValues('quizList'));
    return {
      sessionId,
      draftInstanceId,
      locale: currentLocale,
      countryCode: currentUserCountryCode,
      ...(signal ? {
        signal
      } : {}),
      builderContext: currentBuilderContext,
      selectedQuestionContext,
      ...(turnHints ? {
        turnHints
      } : {}),
      questions,
      questionListRevision: questionListRevisionOf(questions),
      sourceRefs,
      sourceTypes: activeSourceRefs.getSourceTypes(sourceRefs)
    };
  }, [activeSourceRefs, draftInstanceId, getChatEnvironment, getChatState]);
  const ensureServerSessionForSourceRegistration = useCallback(async (runId: number) => {
    return ensureServerSession(runId);
  }, [ensureServerSession]);
  const getNextRequestId = useCallback(() => {
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    return requestId;
  }, []);
  const submitUndoLastAiAction = useAiBuilderSubmitUndoLastAiAction({
    appendUserMessage,
    copy,
    createTurnContext,
    dispatchServerTurn,
    getChatState,
    getNextRequestId,
    isTurnBusy,
    lastQuizListSnapshotRef
  });
  const submitPrompt = useAiBuilderSubmitPrompt({
    activeRunIdRef,
    activeSourceRefs,
    appendUserMessage,
    copy,
    createTurnContext,
    draftInstanceId,
    dispatchServerTurn,
    ensureServerSessionForSourceRegistration,
    getNextRequestId,
    holdSourceRegistration,
    isTurnBusy,
    lastQuizListSnapshotRef,
    getChatState,
    getLatestMessages,
    onAuthExpired: authExpiredHandlers.sourcePreparation,
    selectedQuizContext,
    updateState
  });
  const cancelGeneration = useCallback(() => {
    cancelActiveTurn();
    updateState(prev => ({
      ...prev,
      isGenerating: false,
      turnInProgress: false,
      messages: settleCanceledAiBuilderWorkMessages(prev.messages, copy)
    }));
  }, [cancelActiveTurn, copy, updateState]);
  const resetServerSession = useCallback(() => {
    if (isTurnBusy()) return;
    resetAdapterSession(getChatState().sessionId);
    resetLocalChatSessionState();
  }, [getChatState, isTurnBusy, resetAdapterSession, resetLocalChatSessionState]);
  const registerServerSource = useAiBuilderRegisterServerSource({
    activeSourceRefs,
    appendSystemErrorMessage,
    copy,
    draftInstanceId,
    getChatState,
    holdSourceRegistration,
    isTurnBusy,
    onAuthExpired: authExpiredHandlers.sourceAttach,
    updateState
  });
  const selectChoiceOption = useCallback((messageId: string, optionId: string) => {
    if (isTurnBusy()) return;
    updateState(prev => ({
      ...prev,
      messages: updateChoiceOptionSelection({
        messages: prev.messages,
        messageId,
        optionId
      })
    }));
  }, [isTurnBusy, updateState]);
  const submitConfirmMessage = useAiBuilderSubmitConfirm({
    copy,
    createTurnContext,
    dispatchServerTurn,
    isTurnBusy,
    lastQuizListSnapshotRef,
    getChatState,
    getNextRequestId,
    updateState
  });
  const submitChoiceMessage = useAiBuilderSubmitChoice({
    copy,
    createTurnContext,
    dispatchServerTurn,
    getNextRequestId,
    isTurnBusy,
    lastQuizListSnapshotRef,
    getChatState,
    updateState
  });
  const submitDraft = useCallback(() => {
    const {
      draft: currentDraft,
      messages,
      selectedFile
    } = getChatState();
    const draft = currentDraft.trim();
    const activeConfirmMessage = getActiveConfirmMessage(messages);
    const activeSelectedChoiceMessage = getActiveSelectedChoiceMessage(messages);
    if (activeConfirmMessage) return;
    if (activeSelectedChoiceMessage) {
      submitChoiceMessage(activeSelectedChoiceMessage.id, draft || undefined);
      return;
    }
    const prompt = draft || (selectedFile ? copy.system.defaultFilePrompt : '');
    submitPrompt(prompt);
  }, [copy, getChatState, submitChoiceMessage, submitPrompt]);
  const submitAssistantAction = useAiBuilderSubmitAssistantAction({
    appendUserMessage,
    closePanel,
    copy,
    createTurnContext,
    dispatchServerTurn,
    getChatState,
    isTurnBusy,
    lastQuizListSnapshotRef,
    getNextRequestId,
    selectedQuizContext,
    submitUndoLastAiAction
  });
  const retryUserMessage = useAiBuilderUserMessageRetry({
    beginUserMessageRetry,
    copy,
    createTurnContext,
    dispatchServerTurn,
    getNextRequestId,
    isTurnBusy,
    lastQuizListSnapshotRef,
    getChatState,
    getLatestMessages
  });
  const clearSelectedQuizContext = useCallback(() => {
    setEditingQuizId(null);
  }, [setEditingQuizId]);
  const setResponseFeedback = useAiBuilderResponseFeedback({
    draftInstanceId,
    getChatState,
    updateState
  });
  const actions = useMemo<AiBuilderChatActions>(() => ({
    openFromEntry,
    closePanel,
    setDraft,
    setSelectedFile,
    restartConversation,
    submitDraft,
    submitPrompt,
    cancelGeneration,
    resetServerSession,
    registerServerSource,
    selectChoiceOption,
    submitChoiceMessage,
    submitConfirmMessage,
    submitAssistantAction,
    retryUserMessage,
    requestConfirmActionAttention,
    focusQuizByNumber,
    setResponseFeedback,
    clearSelectedQuizContext
  }), [openFromEntry, closePanel, setDraft, setSelectedFile, restartConversation, submitDraft, submitPrompt, cancelGeneration, resetServerSession, registerServerSource, selectChoiceOption, submitChoiceMessage, submitConfirmMessage, submitAssistantAction, retryUserMessage, requestConfirmActionAttention, focusQuizByNumber, setResponseFeedback, clearSelectedQuizContext]);
  return <AiBuilderChatStoreContext.Provider value={aiBuilderChatStore} data-sentry-element="unknown" data-sentry-component="AiBuilderChatProvider" data-sentry-source-file="AiBuilderChatProvider.tsx">
      <AiBuilderChatActionsContext.Provider value={actions} data-sentry-element="unknown" data-sentry-source-file="AiBuilderChatProvider.tsx">
        <AiBuilderChatComposerSignalContext.Provider value={composerSignalState} data-sentry-element="unknown" data-sentry-source-file="AiBuilderChatProvider.tsx">
          {children}
        </AiBuilderChatComposerSignalContext.Provider>
      </AiBuilderChatActionsContext.Provider>
    </AiBuilderChatStoreContext.Provider>;
};